import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, ToggleLeft, ToggleRight, Save, AlertCircle, X, LogIn, Users, Briefcase } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feature labels mapped to user-friendly names
const FEATURE_LABELS = {
  teams: "Team",
  timesheets: "Timesheets",
  clock_on: "Clock On / Time",
  jobs: "Jobs",
  analytics: "Analytics",
  machines: "Machines",
  prestarts: "Machine Prestarts",
  equipment_registry: "Equipment Registry",
  dry_hires: "Dry Hires / Hire Out",
  daybook: "Daybook (Dockets)",
  inventory: "Inventory",
  forms_library: "Forms Library",
  invoicing: "Invoicing",
  reimbursements: "Reimbursements",
  shadowquote: "ShadowQuote",
  shift_production: "Shift Production Report",
  shift_equipment: "Multi-Machine Shift",
  fuel_pods: "Fuel Pod Tracking",
  incident_log: "Incident & Fault Log",
};

const PLAN_OPTIONS = ["Basic", "Standard", "Pro"];

const SuperAdminPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [catalogue, setCatalogue] = useState({ starter: [], custom_tabs: [], all: [], plans: {} });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draftFeatures, setDraftFeatures] = useState([]);
  const [draftPlan, setDraftPlan] = useState("Basic");
  const [draftShadowquote, setDraftShadowquote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [companiesRes, catalogueRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/super-admin/features`),
      ]);
      setCompanies(companiesRes.data || []);
      setCatalogue(catalogueRes.data || { starter: [], custom_tabs: [], all: [], plans: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const selected = companies.find(c => c.id === selectedId);

  const openCompany = (c) => {
    setSelectedId(c.id);
    setDraftFeatures(c.enabled_features || []);
    setDraftPlan(c.plan || "Basic");
    setDraftShadowquote(!!c.has_shadowquote);
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setDraftFeatures([]);
  };

  const toggleFeature = (key) => {
    setDraftFeatures(prev =>
      prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
    );
  };

  const requestSave = () => {
    // Open the fee/pricing modal first — actual save happens after admin confirms fee
    setShowFeeModal(true);
  };

  const saveFeatures = async (feeRecord) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const beforeFeatures = selected?.enabled_features || [];
      const added = draftFeatures.filter(f => !beforeFeatures.includes(f));
      const removed = beforeFeatures.filter(f => !draftFeatures.includes(f));
      const shadowquoteChange =
        !!selected.has_shadowquote === draftShadowquote ? null :
          (draftShadowquote ? "enabled" : "disabled");

      await axios.put(`${API}/super-admin/companies/${selectedId}/features`, {
        enabled_features: draftFeatures,
        plan: draftPlan,
      });
      if (shadowquoteChange) {
        await axios.put(
          `${API}/super-admin/companies/${selectedId}/shadowquote?enabled=${draftShadowquote}`,
        );
      }

      // Log the subscription event with the fee snapshot
      if (feeRecord) {
        await axios.post(
          `${API}/super-admin/companies/${selectedId}/subscription-events`,
          {
            fee_amount: feeRecord.fee_amount,
            discount_percent: feeRecord.discount_percent,
            waive_fee: feeRecord.waive_fee,
            notes: feeRecord.notes,
            change_summary: feeRecord.summary,
            previous_plan: selected.plan || "Basic",
            new_plan: draftPlan,
            features_added: added,
            features_removed: removed,
            shadowquote_change: shadowquoteChange,
          },
        );
      }

      toast.success("Company updated — changes are now live");
      await fetchAll();
      setShowFeeModal(false);
      closeDrawer();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const viewLiveProfile = async (companyId) => {
    try {
      // Fetch full company doc to set as currentCompany
      const res = await axios.get(`${API}/companies/${companyId}`);
      const company = res.data;
      localStorage.setItem("currentCompanyId", company.id);
      axios.defaults.headers.common["X-Company-Id"] = company.id;
      // Force a full reload so AuthContext re-fetches currentCompany properly
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open company profile");
    }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading text-slate-900">
            Companies
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage subscribed companies. Backdoor sign-in lets you edit any company's live profile.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold transition-colors"
          data-testid="create-company-btn"
        >
          <Plus className="w-5 h-5" />
          New Company
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-400">Loading companies…</div>
      ) : companies.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No companies yet. Create one to get started.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map(c => (
            <CompanyCard
              key={c.id}
              company={c}
              plans={catalogue.plans}
              onEdit={() => openCompany(c)}
              onSignIn={() => viewLiveProfile(c.id)}
            />
          ))}
        </div>
      )}

      {selected && (
        <FeaturesDrawer
          company={selected}
          catalogue={catalogue}
          draftFeatures={draftFeatures}
          draftPlan={draftPlan}
          setDraftPlan={setDraftPlan}
          draftShadowquote={draftShadowquote}
          setDraftShadowquote={setDraftShadowquote}
          onToggle={toggleFeature}
          onClose={closeDrawer}
          onSave={requestSave}
          saving={saving}
          onRefresh={fetchAll}
        />
      )}

      {showFeeModal && selected && (
        <FeeModal
          company={selected}
          draftPlan={draftPlan}
          draftFeatures={draftFeatures}
          draftShadowquote={draftShadowquote}
          onCancel={() => setShowFeeModal(false)}
          onConfirm={(rec) => saveFeatures(rec)}
          saving={saving}
        />
      )}

      {showCreate && (
        <CreateCompanyModal
          starterFeatures={catalogue.starter}
          plans={catalogue.plans}
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await fetchAll(); }}
        />
      )}
    </div>
  );
};

const CompanyCard = ({ company, plans, onEdit, onSignIn }) => {
  const plan = company.plan || "Basic";
  const limit = plans?.[plan] || {};
  const customCount = (company.enabled_features || []).filter(f => !["teams","timesheets","clock_on","jobs","analytics","shadowquote"].includes(f)).length;
  return (
    <div
      className="bg-white border-2 border-slate-200 hover:border-orange-400 rounded-md p-5 transition-colors"
      data-testid={`company-card-${company.code}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
          <Building2 className="w-5 h-5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 truncate">{company.name}</div>
          <div className="text-xs text-slate-500 uppercase font-mono">{company.code}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs mb-3 flex-wrap">
        <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded font-semibold">
          {plan}
        </span>
        {company.is_preview && (
          <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded font-bold uppercase tracking-wide">
            PREVIEW
          </span>
        )}
        {company.has_shadowquote && (
          <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded font-semibold">
            ShadowQuote
          </span>
        )}
        {!company.is_active && (
          <span className="px-2 py-1 bg-red-50 text-red-700 rounded font-semibold">
            DEACTIVATED
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 mb-4 space-y-1">
        <div>{customCount} custom tab{customCount === 1 ? "" : "s"} {limit.max_custom_tabs != null && `/ ${limit.max_custom_tabs}`}</div>
        <div>Employee cap: {limit.max_employees ?? "Unlimited"}</div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onEdit}
          className="flex-1 px-3 py-2 border-2 border-slate-200 hover:border-orange-400 hover:text-orange-600 rounded-md text-sm font-semibold transition-colors"
          data-testid={`edit-company-${company.code}`}
        >
          Edit Plan & Tabs
        </button>
        <button
          onClick={onSignIn}
          className="flex items-center gap-1 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-sm font-semibold transition-colors"
          data-testid={`view-profile-${company.code}`}
          title="View live profile as System Admin"
        >
          <LogIn className="w-4 h-4" />
          View Profile
        </button>
      </div>
    </div>
  );
};

const FeaturesDrawer = ({ company, catalogue, draftFeatures, draftPlan, setDraftPlan, draftShadowquote, setDraftShadowquote, onToggle, onClose, onSave, saving, onRefresh }) => {
  const starter = catalogue.starter || [];
  const customTabs = catalogue.custom_tabs || [];
  const plan = catalogue.plans?.[draftPlan] || {};
  const customCount = draftFeatures.filter(f => customTabs.includes(f)).length;
  const maxCustom = plan.max_custom_tabs;
  const overLimit = maxCustom != null && customCount > maxCustom;

  // ----- User recovery -----
  const [assignments, setAssignments] = useState({ assigned: [], orphans: [] });
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/companies/${company.id}/user-assignments`);
      setAssignments({ assigned: res.data.assigned || [], orphans: res.data.orphans || [] });
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchAssignments();
  }, [company.id]);

  const linkAllOrphans = async () => {
    if (!window.confirm(`Link all ${assignments.orphans.length} unlinked user(s) to ${company.code}?`)) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/super-admin/companies/${company.id}/link-orphan-users`);
      toast.success(res.data.message);
      await fetchAssignments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    } finally {
      setLinking(false);
    }
  };

  const linkOneUser = async () => {
    if (!linkEmail.trim()) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/super-admin/companies/${company.id}/link-user?user_email=${encodeURIComponent(linkEmail.trim())}`);
      toast.success(res.data.message);
      setLinkEmail("");
      await fetchAssignments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    } finally {
      setLinking(false);
    }
  };

  // ----- Change requests -----
  const [requests, setRequests] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [linkedFeature, setLinkedFeature] = useState("");
  const [reqSaving, setReqSaving] = useState(false);
  const [pricingRequest, setPricingRequest] = useState(null); // change request to price

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/companies/${company.id}/change-requests`);
      setRequests(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchRequests();
  }, [company.id]);

  const submitChangeRequest = async () => {
    if (!prompt.trim()) {
      toast.error("Type what you'd like changed first");
      return;
    }
    setReqSaving(true);
    try {
      await axios.post(`${API}/super-admin/companies/${company.id}/change-requests`, {
        prompt: prompt.trim(),
        linked_feature_key: linkedFeature || null,
      });
      toast.success("Request logged — I'll start building shortly. No charge until you activate.");
      setPrompt(""); setLinkedFeature("");
      await fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log request");
    } finally {
      setReqSaving(false);
    }
  };

  const updateRequestStatus = async (rid, status) => {
    try {
      await axios.put(`${API}/super-admin/change-requests/${rid}/status?status=${status}`);
      await fetchRequests();
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const markPaidAndActivate = async (rid) => {
    if (!window.confirm("Confirm payment received and activate change for company employees?")) return;
    try {
      await axios.post(`${API}/super-admin/change-requests/${rid}/mark-paid-activate`);
      toast.success("Paid + activated — employees can see this now");
      await fetchRequests();
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="features-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b-2 border-slate-200 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{company.name}</h2>
            <p className="text-xs text-slate-500 uppercase font-mono">
              {company.code}
              {company.is_preview && <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold">PREVIEW</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-drawer-btn">
            <X className="w-6 h-6" />
          </button>
        </div>

        {company.is_preview && (
          <div className="bg-amber-50 border-b-2 border-amber-200 p-4">
            <div className="text-xs font-bold uppercase text-amber-800 mb-1">In Preview</div>
            <p className="text-xs text-amber-700 mb-3">
              Workers can't log in yet. Once {company.code} has reviewed and paid the first subscription, click below to flip live.
            </p>
            <button
              onClick={async () => {
                if (!window.confirm(`Activate ${company.code}? Workers will be able to log in immediately.`)) return;
                try {
                  await axios.post(`${API}/super-admin/companies/${company.id}/activate-subscription`);
                  toast.success(`${company.code} subscription activated`);
                  onClose();
                  if (onRefresh) await onRefresh();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Activation failed");
                }
              }}
              className="w-full px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded font-bold text-sm"
              data-testid={`activate-subscription-${company.code}`}
            >
              Activate Subscription → Go Live
            </button>
          </div>
        )}

        <div className="p-5 space-y-6">
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">Subscription Plan</label>
            <select
              value={draftPlan}
              onChange={(e) => setDraftPlan(e.target.value)}
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2 font-semibold"
              data-testid="plan-select"
            >
              {PLAN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <div className="mt-2 text-xs text-slate-500 space-y-1">
              <div><span className="font-semibold">Basic:</span> 5 starter tabs · 15 employees</div>
              <div><span className="font-semibold">Standard:</span> 5 starter + 2 custom · 25 employees</div>
              <div><span className="font-semibold">Pro:</span> Unlimited tabs · Unlimited employees</div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Starter Pack</h3>
            <p className="text-xs text-slate-500 mb-3">Locked-in for every company — always on.</p>
            <div className="space-y-2">
              {starter.map(key => (
                <FeatureRow key={key} fkey={key} enabled onToggle={() => {}} starter locked />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Custom Tabs</h3>
              <span className={`text-xs font-bold px-2 py-1 rounded ${overLimit ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                {customCount}{maxCustom != null ? ` / ${maxCustom}` : ""} used
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              These are tailored per company. To add a brand-new tab not listed here, prompt me in chat with the company name and what the tab needs to do — I'll build it just for them.
            </p>
            <div className="space-y-2">
              {customTabs.map(key => (
                <FeatureRow
                  key={key}
                  fkey={key}
                  enabled={draftFeatures.includes(key)}
                  onToggle={() => onToggle(key)}
                  starter={false}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">ShadowQuote Add-On</h3>
            <p className="text-xs text-slate-500 mb-3">
              Sold separately. Currently set up for rigging + fabrication — needs custom config per company.
            </p>
            <button
              onClick={() => setDraftShadowquote(!draftShadowquote)}
              className={`w-full flex items-center justify-between p-3 rounded-md border-2 transition-colors text-left ${
                draftShadowquote ? "bg-purple-50 border-purple-300" : "bg-white border-slate-200 hover:border-slate-400"
              }`}
              data-testid="toggle-shadowquote"
            >
              <div>
                <div className="font-semibold text-slate-900">ShadowQuote Enabled</div>
                <div className="text-xs text-slate-500">Quoting module (extra fee)</div>
              </div>
              {draftShadowquote ? (
                <ToggleRight className="w-7 h-7 text-purple-600" />
              ) : (
                <ToggleLeft className="w-7 h-7 text-slate-400" />
              )}
            </button>
          </div>

          {overLimit && (
            <div className="p-3 bg-red-50 border-2 border-red-200 rounded text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Plan "{draftPlan}" only allows {maxCustom} custom tab{maxCustom === 1 ? "" : "s"}. Upgrade plan or untick some tabs.</span>
            </div>
          )}

          {/* ===== User Recovery / Link Employees ===== */}
          <div className="border-t-2 border-slate-200 pt-5">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Linked Employees</h3>
            <div className="text-xs text-slate-600 mb-3">
              <span className="font-bold">{assignments.assigned.length}</span> currently linked to {company.code}.
              {assignments.orphans.length > 0 && (
                <> <span className="text-orange-700 font-bold">{assignments.orphans.length}</span> orphan user(s) found (in DB but not linked to any company).</>
              )}
            </div>

            {assignments.orphans.length > 0 && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded p-3 mb-3">
                <div className="text-xs font-semibold text-orange-800 mb-2">
                  ⚠ Recovery: these users have no company link. Click below to link them all to {company.code}.
                </div>
                <details className="mb-2">
                  <summary className="text-xs text-slate-600 cursor-pointer">Show orphan users ({assignments.orphans.length})</summary>
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {assignments.orphans.map(u => (
                      <div key={u.id} className="text-xs text-slate-700 bg-white px-2 py-1 rounded border border-slate-200">
                        {u.first_name} {u.last_name} — {u.email} ({u.role})
                      </div>
                    ))}
                  </div>
                </details>
                <button
                  onClick={linkAllOrphans}
                  disabled={linking}
                  className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-md font-semibold text-sm"
                  data-testid="link-orphans-btn"
                >
                  {linking ? "Linking…" : `Link all ${assignments.orphans.length} unlinked user(s) to ${company.code}`}
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                placeholder="Manually link user by email…"
                className="flex-1 border-2 border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="link-user-email"
              />
              <button
                onClick={linkOneUser}
                disabled={linking || !linkEmail.trim()}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-md font-semibold text-sm"
                data-testid="link-user-btn"
              >
                Link
              </button>
            </div>
          </div>

          {/* ===== Client Preview Link ===== */}
          <div className="border-t-2 border-slate-200 pt-5">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Client Preview & Subscription</h3>
            <p className="text-xs text-slate-500 mb-3">
              Generate a one-off link to share with the client. They can walk their full company app in read-only mode, accept T&Cs and price, and submit payment. Once you verify payment, the company goes live.
            </p>
            <ClientPreviewSection company={company} />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t-2 border-slate-200 p-5 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-slate-200 hover:border-slate-400 text-slate-700 rounded-md font-semibold"
            data-testid="cancel-features-btn"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving || overLimit}
            className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md font-semibold flex items-center justify-center gap-2"
            data-testid="save-features-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {pricingRequest && (
        <RequestPriceModal
          request={pricingRequest}
          onCancel={() => setPricingRequest(null)}
          onSaved={async () => { setPricingRequest(null); await fetchRequests(); }}
        />
      )}
    </div>
  );
};

const ClientPreviewSection = ({ company }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [setupFee, setSetupFee] = useState("");
  const [monthly, setMonthly] = useState("");
  const [validDays, setValidDays] = useState(14);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/companies/${company.id}/preview-links`);
      setLinks(res.data || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLinks(); }, [company.id]);

  const create = async () => {
    if (!parseFloat(monthly)) {
      toast.error("Monthly price is required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/super-admin/companies/${company.id}/preview-links`, {
        setup_fee_aud: parseFloat(setupFee) || 0,
        monthly_price_aud: parseFloat(monthly) || 0,
        valid_for_days: parseInt(validDays) || 14,
        contact_name: contactName || null,
        contact_email: contactEmail || null,
        custom_message: customMessage || null,
      });
      toast.success("Preview link generated");
      setShowForm(false);
      setSetupFee(""); setMonthly(""); setContactName(""); setContactEmail(""); setCustomMessage("");
      await fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate link");
    } finally { setCreating(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this preview link? The client will no longer be able to view their preview.")) return;
    try {
      await axios.post(`${API}/super-admin/preview-links/${token}/revoke`);
      toast.success("Revoked");
      await fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  const markPaid = async (token) => {
    if (!window.confirm("Manual override: mark as paid and activate this company now? Only use if payment was received outside Stripe.")) return;
    try {
      await axios.post(`${API}/super-admin/preview-links/${token}/mark-paid-activate`);
      toast.success("Activated");
      await fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/preview/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied — share it with your client");
  };

  const STATUS_BADGE = {
    pending: "bg-amber-100 text-amber-800",
    payment_initiated: "bg-blue-100 text-blue-800",
    paid: "bg-emerald-100 text-emerald-800",
    revoked: "bg-slate-200 text-slate-600",
    expired: "bg-slate-200 text-slate-600",
  };

  return (
    <div data-testid="client-preview-section">
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-sm"
          data-testid="generate-preview-link-btn"
        >
          + Generate Client Preview Link
        </button>
      )}

      {showForm && (
        <div className="bg-slate-50 border-2 border-slate-200 rounded-md p-4 space-y-3" data-testid="generate-preview-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Setup fee (AUD)</label>
              <input type="number" step="0.01" min="0" value={setupFee} onChange={(e) => setSetupFee(e.target.value)}
                placeholder="0.00"
                className="w-full border-2 border-slate-200 rounded px-2 py-1 text-sm"
                data-testid="preview-setup-fee" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Monthly (AUD) *</label>
              <input type="number" step="0.01" min="0" value={monthly} onChange={(e) => setMonthly(e.target.value)}
                placeholder="99.00"
                className="w-full border-2 border-slate-200 rounded px-2 py-1 text-sm"
                data-testid="preview-monthly-fee" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Valid for (days)</label>
            <input type="number" min="1" max="90" value={validDays} onChange={(e) => setValidDays(e.target.value)}
              className="w-full border-2 border-slate-200 rounded px-2 py-1 text-sm"
              data-testid="preview-valid-days" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Client contact name"
              className="border-2 border-slate-200 rounded px-2 py-1 text-sm" data-testid="preview-contact-name" />
            <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Client contact email"
              className="border-2 border-slate-200 rounded px-2 py-1 text-sm" data-testid="preview-contact-email" />
          </div>
          <p className="text-[11px] text-slate-400 -mt-2">Who at the client will review & pay? Pre-fills their form on the preview page.</p>
          <textarea value={customMessage} onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Optional note to show the client when they open the link…" rows={2}
            className="w-full border-2 border-slate-200 rounded px-2 py-1 text-sm"
            data-testid="preview-custom-message" />
          <div className="flex gap-2">
            <button onClick={() => setShowForm(false)} disabled={creating}
              className="flex-1 px-3 py-2 border-2 border-slate-200 hover:border-slate-400 rounded text-sm font-semibold">
              Cancel
            </button>
            <button onClick={create} disabled={creating}
              className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm font-semibold"
              data-testid="preview-create-submit">
              {creating ? "Generating…" : "Generate Link"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-slate-400 mt-3">Loading…</div>
      ) : (
        <div className="space-y-2 mt-3">
          {links.length === 0 && !showForm && (
            <div className="text-xs text-slate-400 italic">No preview links yet for this company.</div>
          )}
          {links.map(l => {
            const url = `${window.location.origin}/preview/${l.token}`;
            return (
              <div key={l.id} className="bg-white border-2 border-slate-200 rounded p-3 text-xs space-y-2" data-testid={`preview-link-row-${l.token.slice(0, 6)}`}>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded font-bold uppercase ${STATUS_BADGE[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status.replace("_", " ")}</span>
                  <span className="text-slate-500">${Number(l.setup_fee_aud || 0).toFixed(2)} setup + ${Number(l.monthly_price_aud || 0).toFixed(2)}/mo</span>
                </div>
                <div className="font-mono text-[11px] bg-slate-50 rounded px-2 py-1 truncate text-slate-700">{url}</div>
                {l.accepted_by_name && (
                  <div className="text-slate-600">Accepted by <span className="font-semibold">{l.accepted_by_name}</span> · {l.accepted_by_email}</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => copyLink(l.token)}
                    className="px-2 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded text-[11px] font-semibold"
                    data-testid={`copy-preview-link-${l.token.slice(0, 6)}`}>Copy link</button>
                  <button onClick={() => window.open(url, "_blank")}
                    className="px-2 py-1 border-2 border-slate-200 hover:border-slate-400 rounded text-[11px] font-semibold">Open</button>
                  {(l.status === "pending" || l.status === "payment_initiated") && (
                    <>
                      <button onClick={() => markPaid(l.token)}
                        className="px-2 py-1 border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded text-[11px] font-semibold"
                        data-testid={`manual-activate-${l.token.slice(0, 6)}`}>Manual activate</button>
                      <button onClick={() => revoke(l.token)}
                        className="px-2 py-1 border-2 border-red-200 text-red-700 hover:bg-red-50 rounded text-[11px] font-semibold"
                        data-testid={`revoke-preview-${l.token.slice(0, 6)}`}>Revoke</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const RequestPriceModal = ({ request, onCancel, onSaved }) => {
  const [fee, setFee] = useState(request.proposed_fee_gross || "");
  const [discount, setDiscount] = useState(request.discount_percent || "");
  const [waive, setWaive] = useState(!!request.waive_fee);
  const [notes, setNotes] = useState(request.fee_notes || "");
  const [saving, setSaving] = useState(false);

  const g = parseFloat(fee) || 0;
  const d = parseFloat(discount) || 0;
  const net = waive ? 0 : Math.max(0, g * (1 - d / 100));

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super-admin/change-requests/${request.id}/price`, {
        proposed_fee: g,
        discount_percent: d,
        waive_fee: waive,
        notes,
      });
      toast.success("Sent for payment — waiting on payment to activate for employees");
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set price");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="request-price-modal">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 border-slate-200 p-5 sticky top-0 bg-white">
          <h2 className="text-xl font-bold uppercase tracking-tight">Send for Payment</h2>
          <p className="text-xs text-slate-500 mt-1">After payment is received, changes go live for employees.</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 rounded p-3 text-xs">
            <div className="font-semibold uppercase mb-1 text-slate-500">Build</div>
            <div className="text-slate-700 whitespace-pre-wrap">{request.prompt}</div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Fee (AUD)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="0.00"
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
              data-testid="request-price-fee"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Discount %</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="0"
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
              data-testid="request-price-discount"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={waive}
              onChange={(e) => setWaive(e.target.checked)}
              className="w-5 h-5"
              data-testid="request-price-waive"
            />
            <span className="text-sm font-semibold text-slate-700">Waive the fee</span>
          </label>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="request-price-notes"
            />
          </div>

          <div className="bg-purple-50 border-2 border-purple-200 rounded p-3 text-sm font-semibold flex items-center justify-between">
            <span>Amount payable</span>
            <span className="text-lg">{waive ? "WAIVED" : `$${net.toFixed(2)}`}</span>
          </div>
        </div>
        <div className="border-t-2 border-slate-200 p-5 flex gap-3 sticky bottom-0 bg-white">
          <button onClick={onCancel} disabled={saving} className="flex-1 px-4 py-3 border-2 border-slate-200 hover:border-slate-400 rounded-md font-semibold">Cancel</button>
          <button onClick={submit} disabled={saving} className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-md font-semibold" data-testid="request-price-submit">
            {saving ? "Saving…" : "Send for Payment"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FeatureRow = ({ fkey, enabled, onToggle, starter, locked }) => (
  <button
    onClick={locked ? undefined : onToggle}
    disabled={locked}
    className={`w-full flex items-center justify-between p-3 rounded-md border-2 transition-colors text-left ${
      enabled
        ? (starter ? "bg-slate-50 border-slate-300" : "bg-orange-50 border-orange-300")
        : "bg-white border-slate-200 hover:border-slate-400"
    } ${locked ? "cursor-default" : ""}`}
    data-testid={`feature-toggle-${fkey}`}
  >
    <div>
      <div className="font-semibold text-slate-900">{FEATURE_LABELS[fkey] || fkey}</div>
      <div className="text-xs text-slate-500">
        {locked ? "Starter · always on" : `Custom · ${fkey}`}
      </div>
    </div>
    {enabled ? (
      <ToggleRight className={`w-7 h-7 ${starter ? "text-slate-500" : "text-orange-500"}`} />
    ) : (
      <ToggleLeft className="w-7 h-7 text-slate-400" />
    )}
  </button>
);

const CreateCompanyModal = ({ starterFeatures, plans, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [plan, setPlan] = useState("Basic");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await axios.post(`${API}/companies`, {
        name,
        code: code.toUpperCase(),
        plan,
        enabled_features: starterFeatures,
      });
      toast.success(`Company ${code.toUpperCase()} created`);
      await onCreated();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to create company");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-company-modal">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold">New Company</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border-2 border-red-200 rounded text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Company ID (login code)</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
              placeholder="e.g. ACME"
              required
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2 font-mono uppercase"
              data-testid="new-company-code"
            />
            <p className="text-xs text-slate-400 mt-1">Employees use this on the login screen.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Company Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Constructions"
              required
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
              data-testid="new-company-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Subscription Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
              data-testid="new-company-plan"
            >
              {PLAN_OPTIONS.map(p => {
                const l = plans?.[p];
                const desc = l ? ` — max ${l.max_employees ?? "∞"} emp, ${l.max_custom_tabs ?? "∞"} custom tabs` : "";
                return <option key={p} value={p}>{p}{desc}</option>;
              })}
            </select>
          </div>
          <div className="bg-slate-50 rounded p-3 text-xs text-slate-600">
            <div className="font-semibold mb-1">Auto-included (starter pack):</div>
            <div>{starterFeatures.map(k => FEATURE_LABELS[k] || k).join(", ")}</div>
            <div className="mt-2 text-slate-500">Custom tabs and ShadowQuote can be added after creation.</div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-slate-200 hover:border-slate-400 rounded-md font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold"
              data-testid="submit-new-company"
            >
              {saving ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FeeModal = ({ company, draftPlan, draftFeatures, draftShadowquote, onCancel, onConfirm, saving }) => {
  const [feeAmount, setFeeAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [waive, setWaive] = useState(false);
  const [notes, setNotes] = useState("");

  const planChanged = (company.plan || "Basic") !== draftPlan;
  const beforeFeatures = company.enabled_features || [];
  const added = draftFeatures.filter(f => !beforeFeatures.includes(f));
  const removed = beforeFeatures.filter(f => !draftFeatures.includes(f));
  const sqChanged = !!company.has_shadowquote !== draftShadowquote;

  const fee = parseFloat(feeAmount) || 0;
  const disc = parseFloat(discount) || 0;

  // Tutorial cost: $3 per newly-added or removed custom tab/shadowquote/plan change
  // (matches the user's rule: $3 per change that needs tutorial updates)
  const TUTORIAL_FEE_PER_CHANGE = 3.0;
  const tutorialChangeCount =
    (planChanged ? 1 : 0) +
    added.length +
    removed.length +
    (sqChanged ? 1 : 0);
  const tutorialFee = tutorialChangeCount * TUTORIAL_FEE_PER_CHANGE;

  const subtotal = fee + tutorialFee;
  const net = waive ? 0 : Math.max(0, subtotal * (1 - disc / 100));

  const summary = [
    planChanged && `Plan: ${company.plan || "Basic"} → ${draftPlan}`,
    added.length && `Add tabs: ${added.join(", ")}`,
    removed.length && `Remove tabs: ${removed.join(", ")}`,
    sqChanged && `ShadowQuote: ${draftShadowquote ? "enabled" : "disabled"}`,
  ].filter(Boolean).join(" · ") || "No changes detected";

  const submit = () => {
    onConfirm({
      fee_amount: fee,
      discount_percent: disc,
      waive_fee: waive,
      notes,
      summary,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="fee-modal">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 border-slate-200 p-5 sticky top-0 bg-white">
          <h2 className="text-xl font-bold uppercase tracking-tight">Subscription Update</h2>
          <p className="text-xs text-slate-500 mt-1">{company.name} ({company.code})</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 rounded p-3 text-xs text-slate-700">
            <div className="font-semibold uppercase mb-1 text-slate-500">Preview of changes</div>
            <div>{summary}</div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Update Fee (AUD)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={feeAmount}
              onChange={(e) => setFeeAmount(e.target.value)}
              placeholder="0.00"
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
              data-testid="fee-amount-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Discount %</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="0"
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
              data-testid="discount-input"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={waive}
              onChange={(e) => setWaive(e.target.checked)}
              className="w-5 h-5"
              data-testid="waive-fee-checkbox"
            />
            <span className="text-sm font-semibold text-slate-700">Waive the fee for this update</span>
          </label>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. First month free, ongoing add-on"
              className="w-full border-2 border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="fee-notes-input"
            />
          </div>

          <div className="bg-slate-50 border-2 border-slate-200 rounded p-3 text-xs space-y-1">
            <div className="flex items-center justify-between">
              <span>Plan / tab change fee</span>
              <span className="font-semibold">${fee.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Tutorial updates ({tutorialChangeCount} × ${TUTORIAL_FEE_PER_CHANGE.toFixed(2)})</span>
              <span className="font-semibold">${tutorialFee.toFixed(2)}</span>
            </div>
            {disc > 0 && !waive && (
              <div className="flex items-center justify-between text-orange-700">
                <span>Discount ({disc}%)</span>
                <span className="font-semibold">−${(subtotal * disc / 100).toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-slate-300 mt-1 pt-1 flex items-center justify-between font-bold">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
          </div>

          <div className="bg-orange-50 border-2 border-orange-200 rounded p-3 text-sm font-semibold flex items-center justify-between">
            <span>Amount payable</span>
            <span className="text-lg">{waive ? "WAIVED" : `$${net.toFixed(2)}`}</span>
          </div>

          <p className="text-xs text-slate-500">
            Stripe is not wired yet — this logs the fee event so it can be invoiced later.
            Changes go live immediately on confirm.
          </p>
        </div>
        <div className="border-t-2 border-slate-200 p-5 flex gap-3 sticky bottom-0 bg-white">
          <button
            onClick={onCancel}
            disabled={saving}
            className="flex-1 px-4 py-3 border-2 border-slate-200 hover:border-slate-400 rounded-md font-semibold"
            data-testid="cancel-fee-btn"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold"
            data-testid="confirm-fee-btn"
          >
            {saving ? "Saving…" : "Confirm & Go Live"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPage;
